"use client";

import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import {
  ChevronDown,
  ChevronUp,
  Search,
  HelpCircle,
  PlusCircle,
  MessageSquare,
  Send,
  User,
  Check,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence, type Transition } from "framer-motion";
import { useMetadata } from "@/hooks/useMetadata";
import { sanitizeText } from "@/lib/sanitize";
import PublicLayout from "@/components/PublicLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { toast } from "sonner";
import PublicHero from "@/components/PublicHero";

const EASE = [0.16, 1, 0.3, 1] as const;
const SPRING: Transition = { type: "spring", stiffness: 300, damping: 30 };

export const FAQ_COPY = {
  hero: {
    eyebrow: "Help & Answers",
    title: "Frequently\nAsked Questions",
    placeholder: "Search questions...",
  },
  categories: [
    "General",
    "Using UniUI",
    "Courses & Materials",
    "Payments",
    "Account & Privacy",
  ],
  questions: [
    {
      category: "General",
      question: "What is UniUI?",
      answer:
        "UniUI is an AI-powered academic companion built exclusively for Nigerian university students. Our platform ingests your lecture notes, past questions, and textbooks to deliver precise, source-cited answers in the structured Nigerian-professor format.",
    },
    {
      category: "General",
      question: "How is UniUI different from ChatGPT?",
      answer:
        "UniUI is curriculum-aligned and trained on Nigerian university materials. Unlike ChatGPT, we cite actual sources from your course materials and follow the pedagogical structure familiar to Nigerian students. We also integrate directly with your university's course structure.",
    },
    {
      category: "General",
      question: "Is UniUI free?",
      answer:
        "Yes — the Free Tier gives you 5 questions per day (max 3 per hour) and every study tool stays open. Need more? Top-Up credits start at ₦500 for_10, the 2-week Exam Pass is ₦2,000, and Premium is ₦1,500/month. Full breakdown on our pricing page.",
    },
    {
      category: "General",
      question: "How does the credit and token system work?",
      answer:
        "Every AI action through the ask pipeline costs one question credit; a failed generation is free to retry, and a fresh attempt after a failure costs 50 tokens (or 1 credit if you are out of tokens). On top of that, UniUI has a non-monetary token economy: open the app daily for 10–20 tokens, earn 50 per referral (your friend gets 20 welcome), and convert 100 tokens into 2 question credits or 3 step-wise explanations. A failed payment is repaid in tokens at 100 ≈ ₦500. Tokens expire after 30 days, you can gift up to 45% of your balance per transfer, and they can never be bought or cashed out — by design.",
    },
    {
      category: "Using UniUI",
      question: "How do I ask a question?",
      answer:
        "Navigate to the AI Scholar terminal at /ask. Enter_your_academic_question_in_natural_language, select_your_course_code, and submit. Answers include sources and follow the Nigerian-professor format.",
    },
    {
      category: "Using UniUI",
      question: "What is Socratic mode?",
      answer:
        "Socratic mode guides your learning through a series of probing questions rather than giving direct answers. It helps develop critical thinking skills and deeper understanding of concepts. Toggle this mode when asking questions to receive Socratic guidance.",
    },
    {
      category: "Using UniUI",
      question: "How do I use the study tools?",
      answer:
        "Access study tools from the navigation menu: Enriched Notes for AI-powered summaries, Formula Sheets for mathematical expressions, Flashcards for active recall, and the Quiz Builder to create custom assessments. All tools adapt to your enrolled courses.",
    },
    {
      category: "Courses & Materials",
      question: "How do I add my courses?",
      answer:
        "Your courses are automatically imported when you register with your institutional email. If courses are missing, contact your course representative or use the course request form in your profile settings.",
    },
    {
      category: "Courses & Materials",
      question: "Who uploads the course materials?",
      answer:
        "Materials are contributed by course representatives, lecturers, and students within each institution. We verify authenticity before publication. You can also upload your own materials after they are processed by our AI pipeline.",
    },
    {
      category: "Courses & Materials",
      question: "What if my course does not have materials yet?",
      answer:
        "You can still use the AI Scholar terminal with any course code. Submit your lecture notes via the upload interface, and our system will process them within minutes. Contact your course rep to coordinate uploads.",
    },
    {
      category: "Payments",
      question: "How do I pay for credits?",
      answer:
        "Visit /pay to select your preferred credit package. We_accept_Moniepoint, bank_transfers, and mobile money payments. After_payment, credits are added to your account within minutes.",
    },
    {
      category: "Payments",
      question: "How long does payment verification take?",
      answer:
        "Automatic Moniepoint payments are verified instantly. Bank transfer and mobile money payments require manual verification and may take up to 24 hours during business days.",
    },
    {
      category: "Payments",
      question: "Can I get a refund?",
      answer:
        "Due to the digital nature of our services, all sales are final. However, if you experience technical issues preventing access to paid features, contact support within 24 hours for assistance.",
    },
    {
      category: "Account & Privacy",
      question: "How do I reset my password?",
      answer:
        "Use the Forgot Password link on the login page to receive a reset link via email. For security reasons, password reset links expire after 1 hour.",
    },
    {
      category: "Account & Privacy",
      question: "Is my data private?",
      answer:
        "Yes. Your questions, saved items, and personal data are encrypted and accessible only to you. We do not share your academic history with third parties. See our Privacy Protocol for full details.",
    },
    {
      category: "Account & Privacy",
      question: "How do I delete my account?",
      answer:
        "Navigate to Settings > Privacy & Security to find the account deletion option. This action is irreversible and will remove all your data from our systems within 30 days.",
    },
  ],
  footer: "Still have questions? Contact us at support@uniui.com.ng.",
};

interface CommunityAnswer {
  author: string;
  answer: string;
  created_at: string;
}

interface CommunityQuestion {
  id: string;
  category: string;
  question: string;
  askedBy: string;
  created_at: string;
  answers: CommunityAnswer[];
}

const SEED_COMMUNITY_QUESTIONS: CommunityQuestion[] = [
  {
    id: "q1",
    category: "Courses & Materials",
    question: "Does anyone have the past questions for MTH102 (2024 session)?",
    askedBy: "Chidi O.",
    created_at: "2 hours ago",
    answers: [
      {
        author: "Amina K.",
        answer:
          "Yes, I uploaded them to the MTH102 study group resources folder. You can download the PDF directly from the groups page.",
        created_at: "1 hour ago",
      },
    ],
  },
  {
    id: "q2",
    category: "General",
    question: "What is the office room number of the HOD for Computer Science?",
    askedBy: "Tunde W.",
    created_at: "Yesterday",
    answers: [],
  },
  {
    id: "q3",
    category: "Using UniUI",
    question:
      "Is the GST101 textbook mandatory or can we just study the slides?",
    askedBy: "Anonymous Scholar",
    created_at: "3 days ago",
    answers: [
      {
        author: "Professor Harrison (Faculty)",
        answer:
          "The slides cover about 70% of the material, but the exam questions are drawn directly from the exercises in the mandatory textbook. I advise studying both.",
        created_at: "2 days ago",
      },
    ],
  },
];

function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle,
  itemRef,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  itemRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <div
      className={[
        "overflow-hidden rounded-2xl border bg-card transition-all duration-200",
        isOpen
          ? "border-y/60 shadow-[0_0_20px_rgba(252,209,83,0.08)]"
          : "border-border hover:border-y/40",
      ].join(" ")}
    >
      <button
        ref={itemRef}
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-y/40 focus-visible:ring-inset"
      >
        <span
          className={[
            "text-sm font-bold transition-colors",
            isOpen ? "text-y" : "text-foreground",
          ].join(" ")}
        >
          {question}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={SPRING}
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
            isOpen ? "bg-y text-black" : "bg-muted text-muted-foreground"
          }`}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={SPRING}
          >
            <p className="px-6 pb-5 pt-1 text-sm font-medium leading-relaxed text-muted-foreground border-t border-border/50">
              {sanitizeText(answer)}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPage() {
  useMetadata({ key: "faq" });
  const copy = usePageContent("faq", FAQ_COPY);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [activeCategory, setActiveCategory] = useState("All");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"official" | "community">(
    "official",
  );

  // Community Q&A state
  const [communityQuestions, setCommunityQuestions] = useState<
    CommunityQuestion[]
  >([]);
  const [newLodgeQuestion, setNewLodgeQuestion] = useState("");
  const [newLodgeCategory, setNewLodgeCategory] = useState("General");
  const [lodgeOpen, setLodgeOpen] = useState(false);
  const [newAnswerText, setNewAnswerText] = useState<{ [qId: string]: string }>(
    {},
  );
  const [newAnswerAuthor, setNewAnswerAuthor] = useState<{
    [qId: string]: string;
  }>({});
  const [answeringQuestionId, setAnsweringQuestionId] = useState<string | null>(
    null,
  );

  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const totalQuestions = copy.questions.length;

  // Load and save community questions from LocalStorage
  useEffect(() => {
    const stored = localStorage.getItem("uniui_community_questions");
    if (stored) {
      try {
        setCommunityQuestions(JSON.parse(stored));
      } catch (e) {
        setCommunityQuestions(SEED_COMMUNITY_QUESTIONS);
      }
    } else {
      setCommunityQuestions(SEED_COMMUNITY_QUESTIONS);
      localStorage.setItem(
        "uniui_community_questions",
        JSON.stringify(SEED_COMMUNITY_QUESTIONS),
      );
    }
  }, []);

  const saveCommunityQuestions = (updated: CommunityQuestion[]) => {
    setCommunityQuestions(updated);
    localStorage.setItem("uniui_community_questions", JSON.stringify(updated));
  };

  const filteredQuestions = useMemo(() => {
    return copy.questions.filter((q) => {
      const matchesSearch =
        debouncedSearchQuery === "" ||
        q.question.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      const matchesCategory =
        activeCategory === "All" || q.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [debouncedSearchQuery, activeCategory, copy.questions]);

  const filteredCommunityQuestions = useMemo(() => {
    return communityQuestions.filter((q) => {
      const matchesSearch =
        debouncedSearchQuery === "" ||
        q.question.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        q.answers.some((ans) =>
          ans.answer.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
        );
      const matchesCategory =
        activeCategory === "All" || q.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [debouncedSearchQuery, activeCategory, communityQuestions]);

  // Lodge a new question
  const handleLodgeQuestion = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLodgeQuestion.trim()) {
      toast.error("Please enter a question description.");
      return;
    }

    const newQ: CommunityQuestion = {
      id: `q-${Date.now()}`,
      category: newLodgeCategory,
      question: newLodgeQuestion.trim(),
      askedBy: "You (Scholar)",
      created_at: "Just now",
      answers: [],
    };

    const updated = [newQ, ...communityQuestions];
    saveCommunityQuestions(updated);
    setNewLodgeQuestion("");
    setLodgeOpen(false);
    toast.success("Question lodged successfully in community registry.");
  };

  // Answer a community question
  const handlePostAnswer = (qId: string) => {
    const answerText = newAnswerText[qId] || "";
    const authorText = newAnswerAuthor[qId] || "";

    if (!answerText.trim()) {
      toast.error("Please enter your answer.");
      return;
    }

    const updated = communityQuestions.map((q) => {
      if (q.id === qId) {
        return {
          ...q,
          answers: [
            ...q.answers,
            {
              author: authorText.trim() || "Anonymous Scholar",
              answer: answerText.trim(),
              created_at: "Just now",
            },
          ],
        };
      }
      return q;
    });

    saveCommunityQuestions(updated);
    setNewAnswerText((prev) => ({ ...prev, [qId]: "" }));
    setNewAnswerAuthor((prev) => ({ ...prev, [qId]: "" }));
    setAnsweringQuestionId(null);
    toast.success("Your answer has been submitted!");
  };

  return (
    <PublicLayout>
      <PageTransition>
        <div className="relative overflow-hidden">

          <main className="relative z-10 mx-auto w-full max-w-3xl space-y-12 px-6 py-20 sm:py-28">
            <PublicHero
              eyebrow={copy.hero.eyebrow}
              eyebrowIcon={<HelpCircle size={12} />}
              title={copy.hero.title.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex justify-center mt-6"
              >
                <div className="inline-flex rounded-xl p-1 bg-muted border border-border">
                  <button
                    onClick={() => setActiveTab("official")}
                    className={`rounded-lg px-6 py-2 text-xs font-black uppercase tracking-widest transition-all ${
                      activeTab === "official"
                        ? "bg-y text-black shadow-md"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Official FAQs
                  </button>
                  <button
                    onClick={() => setActiveTab("community")}
                    className={`rounded-lg px-6 py-2 text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                      activeTab === "community"
                        ? "bg-y text-black shadow-md"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <MessageSquare size={13} />
                    Community Q&A
                  </button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
                className="relative mx-auto max-w-md mt-6"
              >
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder={
                    activeTab === "official"
                      ? "Search official questions..."
                      : "Search community Q&A..."
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(sanitizeText(e.target.value))}
                  className="w-full rounded-full border border-border bg-card py-3.5 pl-12 pr-4 text-sm font-medium text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-y focus:ring-2 focus:ring-y/30"
                />
              </motion.div>
            </PublicHero>

            {/* Result count */}
            {debouncedSearchQuery && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground"
              >
                <span className="text-y">
                  {activeTab === "official"
                    ? filteredQuestions.length
                    : filteredCommunityQuestions.length}
                </span>{" "}
                matching results
              </motion.p>
            )}

            {/* Category chips */}
            <section
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Filter by category"
            >
              {["All", ...copy.categories].map((cat) => (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                  className={[
                    "relative rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.15em] transition-all duration-200",
                    activeCategory === cat
                      ? "bg-y text-black shadow-[0_0_16px_rgba(252,209,83,0.35)]"
                      : "border border-border bg-card text-muted-foreground hover:border-y/40 hover:text-foreground",
                  ].join(" ")}
                >
                  {cat}
                  {activeCategory === cat && (
                    <motion.span
                      layoutId="cat-pill"
                      className="absolute inset-0 rounded-full bg-y"
                      style={{ zIndex: -1 }}
                      transition={SPRING}
                    />
                  )}
                </button>
              ))}
            </section>

            {/* Official FAQs Tab Content */}
            {activeTab === "official" && (
              <section className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {filteredQuestions.length > 0 ? (
                    filteredQuestions.map((q, i) => {
                      const key = `${q.category}-${q.question}`;
                      return (
                        <motion.div
                          key={key}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{
                            duration: 0.3,
                            delay: i * 0.03,
                            ease: EASE,
                          }}
                        >
                          <AccordionItem
                            question={q.question}
                            answer={q.answer}
                            isOpen={expandedId === key}
                            onToggle={() =>
                              setExpandedId(expandedId === key ? null : key)
                            }
                          />
                        </motion.div>
                      );
                    })
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="py-14 text-center space-y-3"
                    >
                      <HelpCircle
                        size={32}
                        className="mx-auto text-muted-foreground/40"
                      />
                      <p className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        No matching questions found in official registry.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            )}

            {/* Community Q&A Tab Content */}
            {activeTab === "community" && (
              <section className="space-y-6">
                {/* Lodge a Question Box */}
                <div className="matte-metal p-6 rounded-3xl border border-y/20">
                  {!lodgeOpen ? (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <Sparkles className="text-y shrink-0" size={20} />
                        <div>
                          <h3 className="font-bold text-sm text-foreground uppercase tracking-wider">
                            Lodge a New Question
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            Can’t find what you need? Lodge a ticket and let
                            other scholars answer it.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setLodgeOpen(true)}
                        className="rounded-full bg-y text-black px-5 py-2.5 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 hover:scale-105 transition-transform"
                      >
                        <PlusCircle size={14} />
                        Lodge Ticket
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleLodgeQuestion} className="space-y-4">
                      <h3 className="font-bold text-sm text-foreground uppercase tracking-wider">
                        Lodge New Academic Question
                      </h3>
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                          Category
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {copy.categories.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => setNewLodgeCategory(cat)}
                              className={`rounded-full px-3.5 py-1.5 text-xs font-black uppercase tracking-widest border transition-colors ${
                                newLodgeCategory === cat
                                  ? "bg-y text-black border-y"
                                  : "border-border bg-background text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                          Your Question
                        </label>
                        <textarea
                          placeholder="e.g. Can someone clarify the formula for calculating semantic mapping constraints?"
                          value={newLodgeQuestion}
                          onChange={(e) => setNewLodgeQuestion(e.target.value)}
                          rows={3}
                          className="w-full rounded-xl border border-border bg-background p-3 text-xs text-foreground outline-none resize-none focus:border-y focus:ring-1 focus:ring-y"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setLodgeOpen(false)}
                          className="rounded-xl border border-border px-4 py-2 text-xs font-black uppercase tracking-wider text-muted-foreground hover:bg-muted"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="rounded-xl bg-y text-black px-5 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1.5"
                        >
                          Submit Question
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Community Questions List */}
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {filteredCommunityQuestions.length > 0 ? (
                      filteredCommunityQuestions.map((q) => (
                        <motion.div
                          key={q.id}
                          layout
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-card border border-border rounded-3xl p-6 space-y-4 transition-all hover:border-y/30"
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between border-b border-border/50 pb-3">
                            <span className="text-xs font-black uppercase tracking-widest bg-y/10 text-y px-2.5 py-1 rounded-full">
                              {q.category}
                            </span>
                            <span className="text-xs font-bold text-muted-foreground uppercase flex items-center gap-1">
                              <User size={10} />
                              {q.askedBy} • {q.created_at}
                            </span>
                          </div>

                          {/* Body */}
                          <p className="text-sm font-bold leading-relaxed text-foreground">
                            {q.question}
                          </p>

                          {/* Answers List */}
                          {q.answers.length > 0 && (
                            <div className="bg-background/50 border border-border/40 rounded-2xl p-4 space-y-3.5">
                              <p className="text-xs font-black uppercase tracking-widest text-y/90 flex items-center gap-1">
                                <MessageSquare size={10} />
                                Answers ({q.answers.length})
                              </p>
                              {q.answers.map((ans, idx) => (
                                <div
                                  key={idx}
                                  className="space-y-1 pl-2 border-l border-y/30"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-black text-foreground">
                                      {ans.author}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {ans.created_at}
                                    </span>
                                  </div>
                                  <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                                    {ans.answer}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex items-center justify-between pt-2">
                            <span className="text-xs font-semibold text-muted-foreground uppercase">
                              {q.answers.length === 0
                                ? "No answers yet"
                                : `${q.answers.length} contribution`}
                            </span>

                            {answeringQuestionId !== q.id ? (
                              <button
                                onClick={() => setAnsweringQuestionId(q.id)}
                                className="rounded-full border border-y/40 text-y bg-y/5 px-4.5 py-1.5 text-xs font-black uppercase tracking-wider hover:bg-y hover:text-black transition-colors"
                              >
                                Write Answer
                              </button>
                            ) : (
                              <button
                                onClick={() => setAnsweringQuestionId(null)}
                                className="text-xs font-black uppercase tracking-wider text-muted-foreground hover:underline"
                              >
                                Cancel
                              </button>
                            )}
                          </div>

                          {/* Answer Box Input */}
                          {answeringQuestionId === q.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              className="border-t border-border pt-4 mt-2 space-y-3"
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                                    Your Name (Optional)
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Anonymous Scholar"
                                    value={newAnswerAuthor[q.id] || ""}
                                    onChange={(e) =>
                                      setNewAnswerAuthor((prev) => ({
                                        ...prev,
                                        [q.id]: e.target.value,
                                      }))
                                    }
                                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs text-foreground outline-none focus:border-y"
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                                  Your Answer
                                </label>
                                <textarea
                                  placeholder="Write your answer to help your classmate..."
                                  value={newAnswerText[q.id] || ""}
                                  onChange={(e) =>
                                    setNewAnswerText((prev) => ({
                                      ...prev,
                                      [q.id]: e.target.value,
                                    }))
                                  }
                                  rows={2}
                                  className="w-full rounded-xl border border-border bg-background p-3 text-xs text-foreground outline-none resize-none focus:border-y"
                                />
                              </div>
                              <div className="flex justify-end">
                                <button
                                  onClick={() => handlePostAnswer(q.id)}
                                  className="rounded-xl bg-y text-black px-5 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1.5"
                                >
                                  <Send size={12} />
                                  Post Answer
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      ))
                    ) : (
                      <div className="py-14 text-center space-y-3 border border-dashed border-border rounded-3xl">
                        <MessageSquare
                          size={32}
                          className="mx-auto text-muted-foreground/30"
                        />
                        <p className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground">
                          No community questions match this filter.
                        </p>
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              </section>
            )}

            <div className="border-t border-border pt-8 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {copy.footer}
              </p>
            </div>
          </main>
        </div>
      </PageTransition>
    </PublicLayout>
  );
}